import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  Package as PackageIcon,
  PauseCircle,
  Pencil,
  Plus,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { FilterCard } from "../components/FilterCard";
import { FormField, selectClass } from "../components/FormField";
import { packageStatusOptions } from "../data";
import { useAdminPackages } from "../hooks/useAdminPackages";
import type { Package } from "../types";

type SortKey = keyof Pick<Package, "code" | "name" | "hospital" | "company">;

export function PackagesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.packages;
  const {
    packages,
    loading,
    error,
    usingApi,
    hospitalFilterOptions,
    companyFilterOptions,
  } = useAdminPackages();

  const [toolbarSearch, setToolbarSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>("code");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const [draftStatus, setDraftStatus] = useState("");
  const [draftHospital, setDraftHospital] = useState("");
  const [draftCompany, setDraftCompany] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedHospital, setAppliedHospital] = useState("");
  const [appliedCompany, setAppliedCompany] = useState("");

  const summary = useMemo(
    () => ({
      total: packages.length,
      active: packages.filter((pkg) => pkg.status === "active").length,
      draft: packages.filter((pkg) => pkg.status === "draft").length,
      suspended: packages.filter((pkg) => pkg.status === "suspended").length,
    }),
    [packages],
  );

  const applyFilters = () => {
    setAppliedStatus(draftStatus);
    setAppliedHospital(draftHospital);
    setAppliedCompany(draftCompany);
    setPage(1);
  };

  const resetFilters = () => {
    setToolbarSearch("");
    setTableSearch("");
    setDraftStatus("");
    setDraftHospital("");
    setDraftCompany("");
    setAppliedStatus("");
    setAppliedHospital("");
    setAppliedCompany("");
    setPage(1);
  };

  const filtered = useMemo(() => {
    return packages.filter((pkg) => {
      const haystack =
        `${pkg.code} ${pkg.name} ${pkg.hospital} ${pkg.company}`.toLowerCase();

      const byToolbarSearch = toolbarSearch ? haystack.includes(toolbarSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      const byStatus = appliedStatus ? pkg.status === appliedStatus : true;
      const byHospital = appliedHospital
        ? pkg.hospitalId === appliedHospital || pkg.hospital === appliedHospital
        : true;
      const byCompany = appliedCompany
        ? pkg.companyId === appliedCompany || pkg.company === appliedCompany
        : true;

      return byToolbarSearch && byTableSearch && byStatus && byHospital && byCompany;
    });
  }, [packages, toolbarSearch, tableSearch, appliedStatus, appliedHospital, appliedCompany]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const cmp = String(a[sortKey]).localeCompare(String(b[sortKey]));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const handleExportCsv = () => {
    const headers = [
      labels.columns.code,
      labels.columns.name,
      labels.columns.hospital,
      labels.fields.company,
      labels.columns.rate,
    ];
    const rows = [
      headers.join(","),
      ...sorted.map((pkg) =>
        [pkg.code, pkg.name, pkg.hospital, pkg.company]
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      ),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "packages.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const summaryCards = [
    {
      label: labels.summary.total,
      value: summary.total,
      valueClass: "text-foreground",
      icon: PackageIcon,
      color: "text-purple",
      bg: "bg-purple-soft",
    },
    {
      label: labels.summary.active,
      value: summary.active,
      valueClass: "text-green",
      icon: CheckCircle,
      color: "text-green",
      bg: "bg-teal-soft",
    },
    {
      label: labels.summary.draft,
      value: summary.draft,
      valueClass: "text-yellow",
      icon: Pencil,
      color: "text-yellow",
      bg: "bg-yellow-soft",
    },
    {
      label: labels.summary.suspended,
      value: summary.suspended,
      valueClass: "text-pink",
      icon: PauseCircle,
      color: "text-pink",
      bg: "bg-pink-soft",
    },
  ];

  const columns: { key: SortKey | "actions"; label: string }[] = [
    { key: "code", label: labels.columns.code },
    { key: "name", label: labels.columns.name },
    { key: "hospital", label: labels.columns.hospital },
    { key: "company", label: labels.fields.company },
    { key: "actions", label: "" },
  ];

  return (
    <>
      <Breadcrumbs items={[{ label: labels.title }]} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
          {usingApi && (
            <p className="mt-1 text-xs text-muted">
              {loading ? "Chargement…" : `${packages.length} package(s) API`}
            </p>
          )}
          {error && <p className="mt-1 text-xs text-pink">{error}</p>}
        </div>
        <Link
          to="/dashboard/packages/create"
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
        >
          <Plus size={16} />
          {labels.add}
        </Link>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="theme-card theme-card-interactive p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted">{card.label}</p>
                <p className={`mt-1 text-2xl font-bold ${card.valueClass}`}>{card.value}</p>
              </div>
              <div className={`flex h-10 w-10 items-center justify-center rounded-full ${card.bg}`}>
                <card.icon size={20} className={card.color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <FilterCard
        title={t.safecheck.filters}
        onFilter={applyFilters}
        onReset={resetFilters}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField label={labels.fields.status}>
            <select
              value={draftStatus}
              onChange={(event) => setDraftStatus(event.target.value)}
              className={selectClass}
            >
              {packageStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value ? option.label : labels.fields.allStatuses}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.fields.hospital}>
            <select
              value={draftHospital}
              onChange={(event) => setDraftHospital(event.target.value)}
              className={selectClass}
            >
              {hospitalFilterOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.fields.allHospitals}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.fields.company}>
            <select
              value={draftCompany}
              onChange={(event) => setDraftCompany(event.target.value)}
              className={selectClass}
            >
              {companyFilterOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.fields.allCompanies}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </FilterCard>

      <div className="theme-card">
        <TableToolbar
          search={toolbarSearch}
          onSearchChange={(value) => {
            setToolbarSearch(value);
            setPage(1);
          }}
          searchPlaceholder={labels.searchPlaceholder}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={labels.pagination.entries}
          onExport={handleExportCsv}
          exportLabel={labels.exportCsv}
          onReset={resetFilters}
          resetLabel={t.safecheck.reset}
        />

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-4 py-3 text-[11px] font-semibold tracking-wide text-muted ${
                      column.key === "actions" ? "text-right" : "text-left"
                    }`}
                  >
                    {column.key === "actions" ? null : (
                      <button
                        type="button"
                        onClick={() => toggleSort(column.key as SortKey)}
                        className="inline-flex items-center gap-1 uppercase"
                      >
                        {column.label}
                        <span className="flex flex-col">
                          <ChevronUp
                            size={10}
                            className={`-mb-1 ${sortKey === column.key && sortDir === "asc" ? "text-primary" : ""}`}
                          />
                          <ChevronDown
                            size={10}
                            className={sortKey === column.key && sortDir === "desc" ? "text-primary" : ""}
                          />
                        </span>
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((pkg, index) => (
                <tr
                  key={pkg.id}
                  className={`border-b border-border last:border-0 hover:bg-card-muted/30 ${
                    index % 2 === 1 ? "bg-card-muted/20" : "bg-card"
                  }`}
                >
                  <td className="px-4 py-3 text-sm font-bold text-primary">
                    <span className="inline-flex items-center gap-2">
                      <ChevronRight size={14} className="text-muted" />
                      {pkg.code}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{pkg.name}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{pkg.hospital}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{pkg.company}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <Link
                        to={`/dashboard/packages/${pkg.id}`}
                        className="inline-flex h-7 w-7 items-center justify-center rounded border border-border text-muted-foreground hover:bg-card-muted"
                        title="Voir"
                      >
                        <Eye size={13} />
                      </Link>
                      <Link
                        to={`/dashboard/packages/${pkg.id}/edit`}
                        className="inline-flex h-7 w-7 items-center justify-center rounded border border-primary/50 text-primary hover:bg-primary-soft"
                        title={t.safecheck.packages.create.editTitle}
                      >
                        <Pencil size={13} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            {labels.pagination.showing} {sorted.length === 0 ? 0 : start + 1} {labels.pagination.to}{" "}
            {Math.min(start + pageSize, sorted.length)} {labels.pagination.of} {sorted.length}{" "}
            {labels.pagination.entries}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-border px-3 py-1.5 transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.previous}
            </button>
            <button
              type="button"
              className="min-w-9 rounded-lg bg-primary px-3 py-1.5 font-medium text-white"
            >
              {currentPage}
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-border px-3 py-1.5 transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.next}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
